import { useEffect, useState } from 'react';
import { ArrowRight, X } from 'lucide-react';

import { Button } from '@/design-system/primitives';
import {
  useSwitchFormat,
  useSwitchFormatQuote,
  type SwitchFormatResult,
} from '@/lib/api/hooks';
import { track } from '@/lib/analytics';
import type { MarketplaceProduct } from '@/types/api';

/**
 * Patient-driven format switch (e.g. NAD injection → NAD nasal) from the
 * shop. Opens off a product card when the patient already owns another
 * format of the same chemical with a still-pending order. Shows the exact
 * money story (due today vs covered, new recurring price) from the quote
 * endpoint before asking for confirmation — same backend machinery as the
 * clinician confirm-task, patient-initiated.
 */

function money(cents: number | null | undefined): string {
  return `$${Math.round((cents ?? 0) / 100)}`;
}

const INELIGIBLE_COPY: Record<string, string> = {
  no_switchable_order:
    'We couldn’t find an active treatment to switch. Message support and we’ll help you change formats.',
  pending_fills:
    'You still have shipments coming in your current cycle, so this switch needs a quick hand from the care team. Message support and we’ll set it up.',
  sub_cancelling:
    'Your current subscription is set to end, so there’s nothing to switch. You can buy this format directly instead.',
  already_has_target: 'You already have this treatment on the way.',
  ambiguous:
    'You have more than one pending treatment we could switch. Message support and we’ll take care of it.',
  no_subscription:
    'This order isn’t tied to an active subscription. Message support to change formats.',
};

export function SwitchFormatModal({
  product,
  onClose,
}: {
  product: MarketplaceProduct;
  onClose: () => void;
}) {
  const canonical = product.canonical_product_id ?? null;
  const quote = useSwitchFormatQuote(canonical);
  const switchFormat = useSwitchFormat();
  const [result, setResult] = useState<SwitchFormatResult | null>(null);
  const isPending = switchFormat.isPending;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isPending]);

  useEffect(() => {
    if (quote.data) {
      track('marketplace_switch_format_quote_viewed', {
        to_product: canonical,
        eligible: quote.data.eligible,
        reason: quote.data.reason,
        due_today_cents: quote.data.due_today_cents,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.data]);

  const q = quote.data;

  const confirm = async () => {
    if (!canonical || isPending) return;
    const r = await switchFormat.mutateAsync({
      to_canonical_product_id: canonical,
    });
    track('marketplace_switch_format_confirmed', {
      to_product: canonical,
      from_product: r.from_product,
      due_paid_cents: r.due_paid_cents,
      outcome: r.fulfillment_outcome,
    });
    setResult(r);
  };

  const outcomeCopy = (r: SwitchFormatResult): string => {
    if (r.mode === 'next_renewal') {
      return `All set — starting ${r.effective_at ?? 'your next renewal'}, you'll receive the new format. Your current treatment and billing are unchanged until then.`;
    }
    if (r.fulfillment_outcome === 'submitted') {
      return 'All set — your updated treatment was sent to the pharmacy.';
    }
    if (r.fulfillment_outcome === 'task_fired') {
      return 'All set — your clinician has been asked to write the updated prescription.';
    }
    return 'Switch applied. Your care team will take it from here.';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-20 bg-black/40 backdrop-blur-sm"
      onClick={() => {
        if (!isPending) onClose();
      }}
    >
      <div
        className="bg-[var(--surface-page)] border border-line-subtle shadow-elevated w-full max-w-[42rem] p-20 flex flex-col gap-15"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-10">
          <div className="type-title-md">Switch your treatment format</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={isPending}
            className="text-ink-secondary hover:text-ink"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {result ? (
          <>
            <p className="type-body-sm">{outcomeCopy(result)}</p>
            {result.due_paid_cents > 0 && (
              <p className="type-body-sm text-ink-secondary">
                Charged today: {money(result.due_paid_cents)}.
              </p>
            )}
            <Button
              size="md"
              variant="primary"
              className="justify-center"
              onClick={onClose}
            >
              Done
            </Button>
          </>
        ) : quote.isLoading ? (
          <p className="type-body-sm text-ink-muted">Checking your plan…</p>
        ) : !q || !q.eligible ? (
          <>
            <p className="type-body-sm text-ink-secondary">
              {INELIGIBLE_COPY[q?.reason ?? ''] ??
                'This switch isn’t available right now. Message support and we’ll help you change formats.'}
            </p>
            <Button
              size="md"
              variant="secondary"
              className="justify-center"
              onClick={onClose}
            >
              Close
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-10 type-body-md">
              <span className="font-semibold">{q.from_product_name}</span>
              <ArrowRight size={16} strokeWidth={1.5} className="shrink-0" />
              <span className="font-semibold">{q.to_product_name}</span>
            </div>
            <p className="type-body-sm text-ink-secondary">
              {q.mode === 'next_renewal'
                ? `Your current shipment and billing stay exactly as they are. Starting ${q.effective_at ?? 'your next renewal'}, you'll receive ${q.to_product_name} instead. No new medical forms needed; your clinician updates the prescription.`
                : `Your pending ${q.from_product_name} treatment becomes ${q.to_product_name} — nothing has shipped yet, so there's no waste and no duplicate subscription. No new medical forms needed; your clinician updates the prescription.`}
            </p>

            <div className="border border-line-subtle bg-surface-subtle p-12 flex flex-col gap-8">
              <div className="flex items-baseline justify-between type-body-sm">
                <span>Due today</span>
                <span className="type-data-md">
                  {q.due_today_cents > 0 ? money(q.due_today_cents) : '$0'}
                </span>
              </div>
              {q.due_today_cents === 0 && (
                <p className="type-label-sm text-ink-muted">
                  {q.mode === 'next_renewal'
                    ? 'Nothing charged today — the new price starts at your next renewal.'
                    : 'Covered by what you’ve already paid — no extra charge for this switch.'}
                </p>
              )}
              {q.due_today_cents > 0 && (
                <p className="type-label-sm text-ink-muted">
                  The price difference for your current{' '}
                  {q.first_cycle ? 'first ' : ''}
                  cycle, charged to your saved card.
                </p>
              )}
              <div className="flex items-baseline justify-between type-body-sm border-t border-line-subtle pt-8">
                <span>
                  New plan
                  {q.mode === 'next_renewal' && q.effective_at
                    ? ` · from ${q.effective_at}`
                    : ''}
                </span>
                <span>
                  {money(q.new_recurring_cents)} every {q.new_interval_days} days
                </span>
              </div>
            </div>

            {switchFormat.isError && (
              <p className="type-body-sm text-status-danger">
                {(switchFormat.error as Error)?.message ??
                  'Could not apply the switch.'}
              </p>
            )}

            <div className="flex gap-8">
              <Button
                size="md"
                variant="primary"
                className="flex-1 justify-center"
                loading={isPending}
                onClick={confirm}
              >
                Confirm switch
                {q.due_today_cents > 0 ? ` · ${money(q.due_today_cents)}` : ''}
              </Button>
              <Button
                size="md"
                variant="ghost"
                onClick={onClose}
                disabled={isPending}
              >
                Never mind
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
